import { S3 } from 'aws-sdk'
import { compact, pick } from 'lodash'
import moment from 'moment'
import 'moment-timezone'
import { redirect } from 'remix-typedjson'
import { convertFrom12To24Format } from './convert-24-to-12-format'
import { envName } from './envName'
import { retry } from './retry'
import { AppointmentType, AppointmentTypeType } from 'app/models/AppointmentType'
import { AvailabilityType } from 'app/models/Availability'
import { IndexProperties } from 'app/models/framework/SearchModel'
import { Location } from 'app/models/Location'
import { ProviderType } from 'app/models/Provider'
import { ResourceSchedule, ResourceScheduleJson } from 'app/models/ResourceSchedule'
import { SchedulingConfiguration, SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'
import { Workspace } from 'app/models/Workspace'
import { KollaAppointment, KollaService } from 'app/services/kolla.server'
import { search } from 'app/services/search.server'
import { debug, logWarn } from 'lib/logging'

export type PatientStatusType = `New Patient` | `Existing Patient`
export const PATIENT_STATUS_TYPES: PatientStatusType[] = [`New Patient`, `Existing Patient`]

export type PatientData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  dob: string
}

export type TimeSlotType = {
  time: string
  availabilities: AvailabilityType[]
}

export type DaySlotType = {
  timeSlots: TimeSlotType[]
  date: string // format: YYYY-MM-DD
}
// used only by agent, online scheduler uses SchedulingConfiguration.granularity
export const GRANULARITY = 30

export const LOOKAHEAD_DAYS = 31

/**
 * Merges consecutive blocks with the same appointment type into continuous time ranges
 * @param notes Array of notes with appointment type and time information
 * @returns Map of appointment type IDs to their merged time blocks
 */
function mergeConsecutiveBlocks(
  notes: Array<{
    note: string
    startTime: string
    endTime: string
    additionalData?: { appointmentType?: string }
  }>,
): Map<string, Array<{ startTime: string; endTime: string }>> {
  const blocksByType = new Map<string, Array<{ startTime: string; endTime: string }>>()

  if (!notes || !Array.isArray(notes)) {
    return blocksByType
  }

  // Group blocks by appointment type
  const groupedBlocks = new Map<string, Array<{ startTime: string; endTime: string }>>()

  for (const note of notes) {
    const appointmentType = note.additionalData?.appointmentType
    if (!appointmentType) continue

    if (!groupedBlocks.has(appointmentType)) {
      groupedBlocks.set(appointmentType, [])
    }

    groupedBlocks.get(appointmentType)!.push({
      startTime: note.startTime,
      endTime: note.endTime,
    })
  }

  // Sort and merge consecutive blocks for each appointment type
  for (const [appointmentType, blocks] of groupedBlocks) {
    // Sort blocks by start time
    const sortedBlocks = blocks.sort((a, b) => {
      return a.startTime.localeCompare(b.startTime)
    })

    const mergedBlocks: Array<{ startTime: string; endTime: string }> = []
    let currentBlock: { startTime: string; endTime: string } | null = null

    for (const block of sortedBlocks) {
      if (!currentBlock) {
        currentBlock = { ...block }
      } else if (currentBlock.endTime === block.startTime) {
        // Blocks are consecutive, merge them
        currentBlock.endTime = block.endTime
      } else {
        // Blocks are not consecutive, save current and start new
        mergedBlocks.push(currentBlock)
        currentBlock = { ...block }
      }
    }

    // Don't forget the last block
    if (currentBlock) {
      mergedBlocks.push(currentBlock)
    }

    blocksByType.set(appointmentType, mergedBlocks)
  }

  return blocksByType
}

/**
 * Merges overlapping or touching schedule blocks into continuous intervals
 * @param schedules Array of resource schedules
 * @param date Date string in YYYY-MM-DD format
 * @returns Array of merged time intervals
 */
function mergeScheduleIntervals(
  schedules: ResourceScheduleJson[],
  date: string,
): Array<{ start: moment.Moment; end: moment.Moment }> {
  const intervals: Array<{ start: moment.Moment; end: moment.Moment }> = []

  // Collect all blocks from all schedules
  schedules.forEach((schedule) => {
    schedule.blocks?.forEach((block) => {
      intervals.push({
        start: moment(`${date} ${block.startTime}`, `YYYY-MM-DD HH:mm`),
        end: moment(`${date} ${block.endTime}`, `YYYY-MM-DD HH:mm`),
      })
    })
  })

  if (intervals.length === 0) return []

  // Sort by start time
  intervals.sort((a, b) => a.start.valueOf() - b.start.valueOf())

  // Merge overlapping or touching intervals
  const merged: Array<{ start: moment.Moment; end: moment.Moment }> = []
  let current = intervals[0]

  for (let i = 1; i < intervals.length; i++) {
    const next = intervals[i]
    // If current end is >= next start, merge them
    if (current.end.isSameOrAfter(next.start)) {
      current.end = moment.max(current.end, next.end)
    } else {
      merged.push(current)
      current = next
    }
  }
  merged.push(current)

  return merged
}

/**
 * Filters available appointment time slots by removing ones that conflict with existing appointments,
 * schedules, or business rules. Enforces provider availability, operatory exclusivity, schedule
 * compliance, and block scheduling constraints.
 *
 * @param slots - Available time slots grouped by day. This array is mutated to remove conflicts.
 * @param appointments - Existing appointments from Kolla system to check for conflicts
 * @param schedules - Resource schedules defining when providers/operatories are available
 * @param providers - Provider information with resource names for lookup
 * @param appointmentType - Type of appointment being scheduled (contains duration and block scheduling rules)
 * @param allowDoubleBooking - Whether to allow up to 2 appointments per provider at same time (default: false)
 *
 * @description
 * This function implements comprehensive conflict detection by checking:
 *
 * 1. **Provider Appointment Conflicts**:
 *    - Normal mode: Blocks slots that overlap with existing provider appointments
 *    - Double booking mode: Allows up to 2 overlapping appointments per provider
 *
 * 2. **Operatory Exclusivity**:
 *    - Always enforced regardless of double booking setting
 *    - Blocks slots if operatory has any conflicting appointments
 *
 * 3. **Schedule Availability**:
 *    - Ensures both provider and operatory are scheduled to be open
 *    - Checks that appointment slot falls entirely within available schedule blocks
 *
 * 4. **Block Scheduling**:
 *    - For appointment types requiring block scheduling (appointmentType.blockScheduling = true)
 *    - Ensures appointment slot falls entirely within designated blocks for that appointment type
 *    - Uses mergeConsecutiveBlocks() to combine adjacent time blocks of same type
 *
 * @example
 * ```typescript
 * const slots = [
 *   { date: '2023-01-15', timeSlots: [{ time: '9:00 AM', availabilities: [...] }] }
 * ];
 *
 * removeOverlaps(slots, appointments, schedules, providers, appointmentType, false);
 * // slots is now mutated to contain only non-conflicting time slots
 * ```
 *
 * @mutates slots - Removes conflicting availabilities and empty time slots from the input array
 *
 * @performance
 * - Uses O(1) lookup maps for providers, appointments, and schedules
 * - Time complexity: O(S × A × (P + O)) where S=slots, A=availabilities, P=provider appointments, O=operatory appointments
 */
export function removeOverlaps(
  slots: DaySlotType[],
  appointments: KollaAppointment[] | undefined,
  schedules: ResourceScheduleJson[] | undefined,
  providers: ProviderType[],
  appointmentType: AppointmentTypeType,
  schedulingConfiguration: SchedulingConfigurationJson | null,
  allowDoubleBooking = false,
) {
  // Early exit if no data to process
  if (!slots.length) return

  // Pre-compute and cache all data structures for O(1) lookups
  const appointmentLengthMinutes = appointmentType.appointmentLength
  const granularity = schedulingConfiguration?.granularity || GRANULARITY

  debug(
    `scheduling`,
    `removeOverlaps:start`,
    JSON.stringify({
      numDays: slots.length,
      numAppointments: appointments?.length ?? 0,
      numSchedules: schedules?.length ?? 0,
      numProviders: providers.length,
      appointmentLengthMinutes,
      allowDoubleBooking,
    }),
  )

  // Index providers by ID for O(1) lookup
  const providerMap = new Map<string, ProviderType>()
  providers.forEach((provider) => providerMap.set(provider.id!, provider))

  // Index appointments by provider resource name and date for faster lookups
  const appointmentsByProviderAndDate = new Map<string, KollaAppointment[]>()
  // Index appointments by operatory resource name and date for operatory-level occupancy checks
  const appointmentsByOperatoryAndDate = new Map<string, KollaAppointment[]>()
  if (appointments) {
    appointments.forEach((appointment) => {
      appointment.providers?.forEach((provider) => {
        if (!provider.name) return
        const appointmentDate = moment(appointment.wall_start_time).format(`YYYY-MM-DD`)
        const key = `${provider.name!}|${appointmentDate}`
        if (!appointmentsByProviderAndDate.has(key)) {
          appointmentsByProviderAndDate.set(key, [])
        }
        appointmentsByProviderAndDate.get(key)!.push(appointment)
      })

      // Index by operatory. Prefer explicit resources array, fallback to appointment.operatory
      const appointmentDate = moment(appointment.wall_start_time).format(`YYYY-MM-DD`)
      if (Array.isArray(appointment.resources)) {
        appointment.resources.forEach((resource) => {
          if (!resource?.name) return
          if (!resource.name.startsWith(`resources/operatory_`)) return
          const key = `${resource.name}|${appointmentDate}`
          if (!appointmentsByOperatoryAndDate.has(key)) {
            appointmentsByOperatoryAndDate.set(key, [])
          }
          appointmentsByOperatoryAndDate.get(key)!.push(appointment)
        })
      }
    })
  }

  debug(
    `scheduling`,
    `removeOverlaps:indexes-built`,
    JSON.stringify({
      appointmentKeys: Array.from(appointmentsByProviderAndDate.keys()).slice(0, 10),
      appointmentKeysCount: appointmentsByProviderAndDate.size,
    }),
  )

  // Index schedules by date and resource name for faster lookups
  const schedulesByDateAndResource = new Map<string, ResourceScheduleJson[]>()
  const operatoryOpenStatus = new Map<string, boolean>() // date|operatoryId -> isOpen

  if (schedules) {
    schedules.forEach((schedule) => {
      if (!schedule.name) return
      const scheduleDate = moment(schedule.date).format(`YYYY-MM-DD`)
      const key = `${scheduleDate}|${schedule.name}`

      if (!schedulesByDateAndResource.has(key)) {
        schedulesByDateAndResource.set(key, [])
      }
      schedulesByDateAndResource.get(key)!.push(schedule)

      // Track operatory open status
      if (schedule.type === `OPERATORY`) {
        const operatoryId = schedule.name.replace(`resources/operatory_`, ``)
        operatoryOpenStatus.set(`${scheduleDate}|${operatoryId}`, true)
      }
    })
  }

  debug(
    `scheduling`,
    `removeOverlaps:schedules-indexed`,
    JSON.stringify({
      scheduleKeysSample: Array.from(schedulesByDateAndResource.keys()).slice(0, 10),
      scheduleKeysCount: schedulesByDateAndResource.size,
      operatoryOpenSample: Array.from(operatoryOpenStatus.keys()).slice(0, 10),
      operatoryOpenCount: operatoryOpenStatus.size,
    }),
  )

  // Process each day slot
  slots.forEach((daySlot, dayIndex) => {
    debug(
      `scheduling`,
      `removeOverlaps:day-start`,
      JSON.stringify({
        dayIndex,
        date: daySlot.date,
        timeSlots: daySlot.timeSlots.length,
      }),
    )
    daySlot.timeSlots.forEach((timeSlot) => {
      const timeSlot24h = convertFrom12To24Format(timeSlot.time)
      const slotStartMoment = moment(`${daySlot.date} ${timeSlot24h}`, `YYYY-MM-DD HH:mm`)
      // Each slot represents one granularity unit (when slots start), not the full appointment duration
      const slotEndMoment = slotStartMoment.clone().add(granularity, `minutes`)

      debug(
        `scheduling`,
        `removeOverlaps:time-slot`,
        JSON.stringify({
          date: daySlot.date,
          time: timeSlot.time,
          time24h: timeSlot24h,
          slotStart: slotStartMoment.format(),
          slotEnd: slotEndMoment.format(),
          availabilities: timeSlot.availabilities.map((a) => ({
            providerId: a.providerId,
            operatoryId: a.operatoryId,
          })),
        }),
      )

      // Filter availabilities for this time slot
      timeSlot.availabilities = timeSlot.availabilities.filter((availability) => {
        const provider = providerMap.get(availability.providerId!)
        if (!provider) {
          debug(
            `scheduling`,
            `removeOverlaps:filter-reason`,
            JSON.stringify({
              date: daySlot.date,
              time: timeSlot.time,
              reason: `provider not found`,
              providerId: availability.providerId,
            }),
          )
          return false
        }

        // Check appointment conflicts (skip if double booking is allowed)
        if (!allowDoubleBooking) {
          debug(
            `scheduling`,
            `removeOverlaps:checking-conflicts`,
            JSON.stringify({
              allowDoubleBooking,
              providerId: availability.providerId,
            }),
          )
          if (!provider.resourceName) return false
          const appointmentKey = `${provider.resourceName}|${daySlot.date}`
          const dayAppointments = appointmentsByProviderAndDate.get(appointmentKey)

          debug(
            `scheduling`,
            `removeOverlaps:availability-check`,
            JSON.stringify({
              providerId: availability.providerId,
              providerResourceName: provider.resourceName,
              operatoryId: availability.operatoryId,
              appointmentKey,
              dayAppointmentsCount: dayAppointments?.length ?? 0,
            }),
          )

          if (dayAppointments) {
            const hasAppointmentConflict = dayAppointments.some((appointment) => {
              const apptStart = moment(appointment.wall_start_time)
              const apptEnd = moment(appointment.wall_end_time)

              // Check for time overlap: slot overlaps with appointment if slot_end > appt_start AND slot_start < appt_end
              const overlap = slotEndMoment.isAfter(apptStart) && slotStartMoment.isBefore(apptEnd)
              if (overlap) {
                debug(
                  `scheduling`,
                  `removeOverlaps:appointment-overlap`,
                  JSON.stringify({
                    providerResourceName: provider.resourceName,
                    appointment: {
                      wall_start_time: appointment.wall_start_time,
                      wall_end_time: appointment.wall_end_time,
                    },
                    slotStart: slotStartMoment.format(),
                    slotEnd: slotEndMoment.format(),
                  }),
                )
              }
              return overlap
            })

            if (hasAppointmentConflict) {
              debug(
                `scheduling`,
                `removeOverlaps:filter-reason`,
                JSON.stringify({
                  date: daySlot.date,
                  time: timeSlot.time,
                  reason: `provider has conflicting appointment`,
                  providerId: availability.providerId,
                  operatoryId: availability.operatoryId,
                }),
              )
              return false
            }
          }
        }
        // When double booking is enabled, we don't check provider-level conflicts.
        // The operatory exclusivity check below ensures no double-booking of operatories.
        // If a provider is configured for multiple operatories, they can have appointments
        // in different operatories at the same time (scheduled by the dental office in their PMS).

        // Check operatory appointment conflicts (always enforced for operatory exclusivity)
        if (availability.operatoryId) {
          const operatoryResourceName = `resources/operatory_${availability.operatoryId}`
          const operatoryAppointmentKey = `${operatoryResourceName}|${daySlot.date}`
          const operatoryAppointments = appointmentsByOperatoryAndDate.get(operatoryAppointmentKey)

          debug(
            `scheduling`,
            `removeOverlaps:operatory-appointment-check`,
            JSON.stringify({
              operatoryAppointmentKey,
              operatoryAppointmentsCount: operatoryAppointments?.length ?? 0,
            }),
          )

          if (operatoryAppointments) {
            const hasOperatoryAppointmentConflict = operatoryAppointments.some((appointment) => {
              const apptStart = moment(appointment.wall_start_time)
              const apptEnd = moment(appointment.wall_end_time)
              // Check if slot overlaps with appointment
              // Slot overlaps if it ends after appointment starts AND starts before appointment ends
              const overlap = slotEndMoment.isAfter(apptStart) && slotStartMoment.isBefore(apptEnd)
              if (overlap) {
                debug(
                  `scheduling`,
                  `removeOverlaps:operatory-overlap`,
                  JSON.stringify({
                    operatoryResourceName,
                    appointment: {
                      id: appointment.remote_id,
                      wall_start_time: appointment.wall_start_time,
                      wall_end_time: appointment.wall_end_time,
                      short_description: appointment.short_description,
                      contact: appointment.contact,
                    },
                    slotStart: slotStartMoment.format(),
                    slotEnd: slotEndMoment.format(),
                  }),
                )
              }
              return overlap
            })

            if (hasOperatoryAppointmentConflict) {
              debug(
                `scheduling`,
                `removeOverlaps:filter-reason`,
                JSON.stringify({
                  date: daySlot.date,
                  time: timeSlot.time,
                  reason: `operatory has conflicting appointment`,
                  providerId: availability.providerId,
                  operatoryId: availability.operatoryId,
                }),
              )
              return false
            }
          }
        }

        // Check if operatory is open - this check applies regardless of double booking setting
        const operatoryKey = `${daySlot.date}|${availability.operatoryId}`
        const isOperatoryOpen = operatoryOpenStatus.has(operatoryKey)
        debug(`scheduling`, `removeOverlaps:operatory-open-check`, JSON.stringify({ operatoryKey, isOperatoryOpen }))
        if (!isOperatoryOpen) {
          debug(
            `scheduling`,
            `removeOverlaps:filter-reason`,
            JSON.stringify({
              date: daySlot.date,
              time: timeSlot.time,
              reason: `operatory not open`,
              providerId: availability.providerId,
              operatoryId: availability.operatoryId,
              operatoryKey,
            }),
          )
          return false
        }

        // Check provider schedule conflicts
        const providerScheduleKey = `${daySlot.date}|${provider.resourceName}`
        const providerSchedules = schedulesByDateAndResource.get(providerScheduleKey)

        if (providerSchedules) {
          const mergedIntervals = mergeScheduleIntervals(providerSchedules, daySlot.date)
          const hasProviderScheduleConflict =
            mergedIntervals.length > 0
              ? !mergedIntervals.some(
                  ({ start, end }) => slotStartMoment.isSameOrAfter(start) && slotEndMoment.isSameOrBefore(end),
                )
              : true

          debug(
            `scheduling`,
            `removeOverlaps:provider-schedule-check`,
            JSON.stringify({
              providerScheduleKey,
              providerSchedulesCount: providerSchedules?.length ?? 0,
              mergedIntervals: mergedIntervals.map((m) => ({
                start: m.start.format(`HH:mm`),
                end: m.end.format(`HH:mm`),
              })),
              hasProviderScheduleConflict,
            }),
          )
          if (hasProviderScheduleConflict) {
            debug(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `provider schedule conflict`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                providerScheduleKey,
              }),
            )
            return false
          }
        }

        // Check operatory schedule conflicts
        const operatoryScheduleKey = `${daySlot.date}|resources/operatory_${availability.operatoryId}`
        const operatorySchedules = schedulesByDateAndResource.get(operatoryScheduleKey)

        if (operatorySchedules) {
          const mergedIntervals = mergeScheduleIntervals(operatorySchedules, daySlot.date)
          const hasOperatoryScheduleConflict =
            mergedIntervals.length > 0
              ? !mergedIntervals.some(
                  ({ start, end }) => slotStartMoment.isSameOrAfter(start) && slotEndMoment.isSameOrBefore(end),
                )
              : true

          debug(
            `scheduling`,
            `removeOverlaps:operatory-schedule-check`,
            JSON.stringify({
              operatoryScheduleKey,
              operatorySchedulesCount: operatorySchedules?.length ?? 0,
              mergedIntervals: mergedIntervals.map((m) => ({
                start: m.start.format(`HH:mm`),
                end: m.end.format(`HH:mm`),
              })),
              hasOperatoryScheduleConflict,
            }),
          )
          if (hasOperatoryScheduleConflict) {
            debug(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `operatory schedule conflict`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                operatoryScheduleKey,
                scheduleBlocks: operatorySchedules?.flatMap((s) => s.blocks),
                slotStart: slotStartMoment.format(),
                slotEnd: slotEndMoment.format(),
              }),
            )
            return false
          }
        }

        // Check block scheduling constraints
        if (appointmentType.blockScheduling && availability.operatoryId && operatorySchedules) {
          // Note: Resource schedule notes store vendorId (e.g., "8"), not full ID (e.g., "appointmenttypes/8")
          const vendorId = appointmentType.vendorId!
          let hasValidBlock = false

          for (const schedule of operatorySchedules) {
            if (schedule.notes && Array.isArray(schedule.notes)) {
              const mergedBlocks = mergeConsecutiveBlocks(schedule.notes)
              debug(
                `scheduling`,
                `removeOverlaps:checking-blocks`,
                JSON.stringify({
                  vendorId,
                  operatoryId: availability.operatoryId,
                  date: daySlot.date,
                  time: timeSlot.time,
                  slotStart: slotStartMoment.format(),
                  slotEnd: slotEndMoment.format(),
                  allBlockKeys: Array.from(mergedBlocks.keys()),
                }),
              )
              // Try matching with just vendor ID first (e.g., "8"), then with full format (e.g., "appointmenttypes/8")
              const blocksForType = mergedBlocks.get(vendorId) || mergedBlocks.get(`appointmenttypes/${vendorId}`)

              if (blocksForType) {
                debug(
                  `scheduling`,
                  `removeOverlaps:blocks-found`,
                  JSON.stringify({
                    vendorId,
                    blocksCount: blocksForType.length,
                    blocks: blocksForType.map((b) => ({ start: b.startTime, end: b.endTime })),
                  }),
                )
                hasValidBlock = blocksForType.some((block) => {
                  const blockStart = moment(`${daySlot.date} ${block.startTime}`, `YYYY-MM-DD HH:mm:ss`)
                  const blockEnd = moment(`${daySlot.date} ${block.endTime}`, `YYYY-MM-DD HH:mm:ss`)

                  const matches = slotStartMoment.isSameOrAfter(blockStart) && slotEndMoment.isSameOrBefore(blockEnd)
                  debug(
                    `scheduling`,
                    `removeOverlaps:block-match-check`,
                    JSON.stringify({
                      blockStart: blockStart.format(),
                      blockEnd: blockEnd.format(),
                      slotStart: slotStartMoment.format(),
                      slotEnd: slotEndMoment.format(),
                      matches,
                    }),
                  )
                  // Slot must fall ENTIRELY within the block
                  // Slot start must be at or after block start, and slot end must be at or before block end
                  return matches
                })

                if (hasValidBlock) break
              } else {
                debug(
                  `scheduling`,
                  `removeOverlaps:no-blocks-for-type`,
                  JSON.stringify({
                    vendorId,
                    alternateKey: `appointmenttypes/${vendorId}`,
                  }),
                )
              }
            }
          }

          if (!hasValidBlock) {
            debug(
              `scheduling`,
              `removeOverlaps:block-scheduling-violation`,
              JSON.stringify({
                vendorId,
                operatoryId: availability.operatoryId,
                slotStart: slotStartMoment.format(),
                slotEnd: slotEndMoment.format(),
              }),
            )
            debug(
              `scheduling`,
              `removeOverlaps:filter-reason`,
              JSON.stringify({
                date: daySlot.date,
                time: timeSlot.time,
                reason: `block scheduling violation`,
                providerId: availability.providerId,
                operatoryId: availability.operatoryId,
                vendorId,
              }),
            )
            return false
          }
        }

        // Check blockout exclusions (mutually exclusive with block scheduling)
        // excludedBlockouts contains appointment type refs like "appointmenttypes/3"
        if (
          !appointmentType.blockScheduling &&
          appointmentType.excludedBlockouts?.length &&
          availability.operatoryId &&
          operatorySchedules
        ) {
          for (const schedule of operatorySchedules) {
            if (schedule.notes && Array.isArray(schedule.notes)) {
              for (const blockout of schedule.notes) {
                const blockoutApptType = blockout.additionalData?.appointmentType
                // Normalize to full reference format (excludedBlockouts stores 'appointmenttypes/X')
                const blockoutRef = blockoutApptType?.startsWith(`appointmenttypes/`)
                  ? blockoutApptType
                  : `appointmenttypes/${blockoutApptType}`
                if (!blockoutApptType || !appointmentType.excludedBlockouts.includes(blockoutRef)) continue
                if (!blockout.startTime || !blockout.endTime) continue

                const blockoutStart = moment(`${daySlot.date} ${blockout.startTime}`, `YYYY-MM-DD HH:mm:ss`)
                const blockoutEnd = moment(`${daySlot.date} ${blockout.endTime}`, `YYYY-MM-DD HH:mm:ss`)

                // Slot overlaps blockout if: slotEnd > blockoutStart AND slotStart < blockoutEnd
                if (slotEndMoment.isAfter(blockoutStart) && slotStartMoment.isBefore(blockoutEnd)) {
                  debug(
                    `scheduling`,
                    `removeOverlaps:blockout-exclusion`,
                    JSON.stringify({
                      blockoutApptType,
                      blockoutStart: blockoutStart.format(),
                      blockoutEnd: blockoutEnd.format(),
                      slotStart: slotStartMoment.format(),
                      slotEnd: slotEndMoment.format(),
                    }),
                  )
                  debug(
                    `scheduling`,
                    `removeOverlaps:filter-reason`,
                    JSON.stringify({
                      date: daySlot.date,
                      time: timeSlot.time,
                      reason: `blockout exclusion`,
                      providerId: availability.providerId,
                      operatoryId: availability.operatoryId,
                      blockoutApptType,
                    }),
                  )
                  return false
                }
              }
            }
          }
        }

        return true
      })

      debug(
        `scheduling`,
        `removeOverlaps:time-slot-after-filter`,
        JSON.stringify({
          date: daySlot.date,
          time: timeSlot.time,
          remainingAvailabilityProviders: timeSlot.availabilities.map((a) => a.providerId),
        }),
      )
    })

    // Filter out empty time slots and sort by time
    daySlot.timeSlots = daySlot.timeSlots
      .filter((timeSlot) => timeSlot.availabilities.length > 0)
      .sort((a, b) => {
        const timeA = moment(a.time, `h:mm A`)
        const timeB = moment(b.time, `h:mm A`)
        return timeA.valueOf() - timeB.valueOf()
      })

    debug(
      `scheduling`,
      `removeOverlaps:day-after-filter`,
      JSON.stringify({
        dayIndex,
        date: daySlot.date,
        remainingTimes: daySlot.timeSlots.map((t) => t.time),
      }),
    )
  })
}

async function getInternalSchedules(location: Location, day?: string) {
  const currentDay = day ? moment(day) : moment()
  // Always fetch from start of month to end of month to ensure consistent availability
  const firstDay = moment(currentDay).startOf(`month`)
  const lastDay = moment(currentDay).endOf(`month`).format(`YYYY-MM-DD`)
  return (
    await search.query(
      ResourceSchedule,
      [
        `orgId:${location.orgId}`,
        `workspaceId:${location.workspaceId}`,
        `kollaConsumerId:${location.kollaConsumerId}`,
        `date:[${firstDay.format(`YYYY-MM-DD`)} TO ${lastDay}]`,
      ],
      {
        limit: 10000 as any,
      },
    )
  ).hits
}

async function getExternalAppointments(location: Location, day?: string): Promise<Partial<KollaAppointment>[]> {
  const currentDay = day ? moment(day) : moment()
  // Always fetch from start of month to end of month to ensure consistent availability
  const firstDay = moment(currentDay).startOf(`month`)
  const lastDay = moment(currentDay).endOf(`month`)
  const kollaService = await KollaService.getInstance(location.orgId!)
  const appointments = await kollaService.getAppointments(location, firstDay, lastDay)

  return compact(
    appointments?.map((appointment) => pick(appointment, [`id`, `wall_start_time`, `wall_end_time`, `providers`])),
  )
}

export type SchedulingLoaderData = {
  day: string
  lookaheadDays: number
  appointmentTypes: AppointmentTypeType[]
  logoUrl: Promise<string>
  providersPromise: Promise<ProviderType[]>
  availabilitiesPromise: Promise<AvailabilityType[]>
  internalSchedulesPromise: Promise<IndexProperties<ResourceSchedule>[]>
  externalAppointmentsPromise: Promise<Partial<KollaAppointment>[]>
  schedulingConfiguration: SchedulingConfigurationJson | null
  timing: Record<string, number>
}

export type ProcessedSchedulingData = {
  day: string
  lookaheadDays: number
  appointmentTypes: AppointmentTypeType[]
  logoUrl: Promise<string>
  providers: ProviderType[]
  availabilities: AvailabilityType[]
  slots: DaySlotType[]
  schedulingConfiguration: SchedulingConfigurationJson | null
  timing: Record<string, number>
}

/**
 * Loads only appointment types for a location.
 * This is a fast operation that allows the UI to render the appointment type selector
 * while the rest of the scheduling data is still loading.
 *
 * @param locationId - The location ID to load appointment types for
 * @returns Array of active appointment types for the location
 */
export async function getAppointmentTypes(locationId: string): Promise<AppointmentTypeType[]> {
  if (!locationId) {
    logWarn(`getAppointmentTypes: locationId is empty or null`, { locationId })
    throw redirect(`/`)
  }

  const location = await Location.getById(locationId, undefined)

  if (!location) {
    logWarn(`getAppointmentTypes: location not found`, { locationId })
    throw redirect(`/`)
  }
  if (!location.isSchedulerLive) {
    logWarn(`scheduling is not enabled for location`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }

  const workspace = await Workspace.getById(location.workspaceId, location.orgId)

  if (!workspace || !workspace.products?.includes(`Scheduling`)) {
    logWarn(`scheduling is not enabled for workspace`, location.workspaceId)
    throw redirect(`/scheduling/unavailable`)
  }

  let { hits: appointmentTypes } = await search.query(
    AppointmentType,
    [
      `orgId:${location.orgId}`,
      `workspaceId:${location.workspaceId}`,
      `kollaConsumerId:${location.kollaConsumerId}`,
      `activeScheduler:true`,
      `vendor:kolla`,
      `!archived:true`,
    ],
    {
      limit: 1000,
      page: 0,
      sort: `displayName.keyword`,
    },
  )

  const kollaService = await KollaService.getInstance(location.orgId!)
  const externalAppointmentTypes = await kollaService.getAppointmentTypes(location)

  if (!externalAppointmentTypes) {
    logWarn(`failed to get external appointment types`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }

  appointmentTypes = appointmentTypes.filter(
    (at) => !!externalAppointmentTypes.find((eat) => eat.remote_id === at.vendorId),
  )

  return appointmentTypes
}

export async function loadSchedulingData(locationId: string, day?: string): Promise<SchedulingLoaderData> {
  const START_TIME = performance.now()
  const timings: Record<string, number> = {}

  const validDay = day ?? moment().format(`YYYY-MM-DD`)
  // Allow dates within the current month or future, but reset dates before the current month
  if (!moment(validDay).isValid() || moment(validDay).isBefore(moment().startOf(`month`))) {
    logWarn(`Invalid day parameter`, day, `setting to start of current month`)
    day = moment().startOf(`month`).format(`YYYY-MM-DD`)
  } else {
    day = validDay
  }
  const lookaheadDays = day ? moment(day).endOf(`month`).diff(day, `days`) + 1 : LOOKAHEAD_DAYS

  if (!locationId) {
    logWarn(`loadSchedulingData: locationId is empty or null`, { locationId })
    throw redirect(`/`)
  }

  // Location lookup timing
  const locationStart = performance.now()
  const location = await Location.getById(locationId, undefined)
  timings.location = performance.now() - locationStart

  if (!location) {
    logWarn(`loadSchedulingData: location not found`, { locationId })
    throw redirect(`/`)
  }
  if (!location.isSchedulerLive) {
    logWarn(`scheduling is not enabled for location`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }

  // Workspace lookup timing
  const workspaceStart = performance.now()
  const workspace = await Workspace.getById(location.workspaceId, location.orgId)
  timings.workspace = performance.now() - workspaceStart

  if (!workspace || !workspace.products?.includes(`Scheduling`)) {
    logWarn(`scheduling is not enabled for workspace`, location.workspaceId)
    throw redirect(`/scheduling/unavailable`)
  }

  // Appointment types timing
  const appointmentTypesStart = performance.now()
  let { hits: appointmentTypes } = await search.query(
    AppointmentType,
    [
      `orgId:${location.orgId}`,
      `workspaceId:${location.workspaceId}`,
      `kollaConsumerId:${location.kollaConsumerId}`,
      `activeScheduler:true`,
      `vendor:kolla`,
      `!archived:true`,
    ],
    {
      limit: 1000,
      page: 0,
      sort: `displayName.keyword`,
    },
  )
  timings.appointmentTypesQuery = performance.now() - appointmentTypesStart

  // External appointment types timing
  const externalTypesStart = performance.now()
  const kollaService = await KollaService.getInstance(location.orgId!)
  const externalAppointmentTypes = await kollaService.getAppointmentTypes(location)
  timings.externalAppointmentTypes = performance.now() - externalTypesStart

  if (!externalAppointmentTypes) {
    logWarn(`failed to get external appointment types`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }
  appointmentTypes = appointmentTypes.filter(
    (at) => !!externalAppointmentTypes.find((eat) => eat.remote_id === at.vendorId),
  )

  const s3 = new S3()
  const s3Params = {
    Bucket: `trackable-${envName()}-assets`,
    Key: `scheduling-logos/locations/${location.id}`,
  }

  // Start all async operations with timing
  const providerStart = performance.now()
  const providersPromise = location.getProviders(`scheduler`)
  providersPromise
    .then(() => {
      timings.providers = performance.now() - providerStart
    })
    .catch(() => {
      // Ignore timing errors
    })

  const availabilityStart = performance.now()
  const availabilitiesPromise = location.getAvailabilities(`scheduler`)
  availabilitiesPromise
    .then(() => {
      timings.availabilities = performance.now() - availabilityStart
    })
    .catch(() => {
      // Ignore timing errors
    })

  const internalSchedulesStart = performance.now()
  const internalSchedulesPromise = getInternalSchedules(location, day)
  internalSchedulesPromise
    .then(() => {
      timings.internalSchedules = performance.now() - internalSchedulesStart
    })
    .catch(() => {
      // Ignore timing errors
    })

  const externalApptsStart = performance.now()
  const externalAppointmentsPromise = getExternalAppointments(location, day)
  externalAppointmentsPromise
    .then(() => {
      timings.externalAppointments = performance.now() - externalApptsStart
    })
    .catch(() => {
      // Ignore timing errors
    })

  const configStart = performance.now()
  const schedulingConfiguration =
    ((await search.findOne(SchedulingConfiguration, [`locationId:${location.id}`])) as SchedulingConfigurationJson) ||
    null
  timings.schedulingConfiguration = performance.now() - configStart

  const TOTAL_TIME = performance.now() - START_TIME
  logWarn(`Scheduling Page Loader Timings:`, {
    total: TOTAL_TIME,
    ...timings,
  })

  return {
    day,
    lookaheadDays,
    appointmentTypes,
    logoUrl: s3.getSignedUrlPromise(`getObject`, s3Params),
    providersPromise,
    availabilitiesPromise,
    internalSchedulesPromise,
    externalAppointmentsPromise,
    schedulingConfiguration,
    timing: { total: TOTAL_TIME, ...timings },
  }
}

/**
 * Generates all available time slots for all appointment types by processing provider availabilities.
 * This is the server-side version of the slot generation logic from SchedulingWizard.
 *
 * @param appointmentTypes - Available appointment types
 * @param providers - Provider data
 * @param availabilities - Provider availability data
 * @param appointments - Existing appointments for conflict detection
 * @param schedules - Resource schedules for blocking
 * @param location - Location data for timezone handling
 * @param startDate - Start date for slot generation
 * @param lookaheadDays - Number of days to generate slots for
 * @param schedulingConfiguration - Configuration including granularity and double booking settings
 * @returns Array of day slots with time slots for all appointment types
 */
function generateAllSlots(
  appointmentTypes: AppointmentTypeType[],
  providers: ProviderType[],
  availabilities: AvailabilityType[],
  appointments: Partial<KollaAppointment>[],
  schedules: IndexProperties<ResourceSchedule>[],
  location: Location,
  startDate: string,
  lookaheadDays: number,
  schedulingConfiguration: SchedulingConfigurationJson | null,
): DaySlotType[] {
  // Generate slots for all appointment types combined
  // This creates a union of all possible slots across all appointment types
  const allSlots: DaySlotType[] = new Array(lookaheadDays)
  const startDay = moment(startDate).day()
  const userTimezone = moment.tz.guess()
  const locationTimezone = location?.timezone ?? userTimezone

  if (!schedulingConfiguration?.granularity) {
    debug(`scheduling`, `generateAllSlots: No granularity configured, using default`, GRANULARITY)
    return []
  }

  // For each appointment type, generate slots and merge them
  appointmentTypes.forEach((appointmentType) => {
    debug(`scheduling`, `generateAllSlots: Processing appointment type`, {
      appointmentTypeId: appointmentType.id,
      vendorId: appointmentType.vendorId,
      displayName: appointmentType.displayName,
      totalAvailabilities: availabilities.length,
    })

    // Filter availabilities for this appointment type
    const workingAvailabilities = availabilities.filter((a) => {
      const matches = a.appointmentTypeIds?.includes(appointmentType.vendorId!)
      debug(`scheduling`, `generateAllSlots: Checking availability`, {
        providerId: a.providerId,
        operatoryId: a.operatoryId,
        appointmentTypeIds: a.appointmentTypeIds,
        vendorId: appointmentType.vendorId,
        matches,
      })
      return matches
    })

    debug(`scheduling`, `generateAllSlots: Filtered availabilities`, {
      appointmentTypeId: appointmentType.id,
      workingAvailabilitiesCount: workingAvailabilities.length,
    })

    // For each availability, process all days of the week
    workingAvailabilities.forEach((availability) => {
      Object.entries(availability.timeSlots!).forEach(([dayOfTheWeek, { hours, open }]) => {
        // Skip if provider doesn't have timeslots for this day
        if (!open) {
          return
        }

        // Get the day of the week for the availability timeslots
        const day = moment(startDate).day(dayOfTheWeek).day()
        // Calculate the offset from the start day to the day of the week of the availability
        const offset = (day + 7 - startDay) % 7

        // Access the slots array at the offset and every 7 days after that
        for (let i = 0; i <= lookaheadDays / 7; i++) {
          const index = offset + i * 7
          // If the index is greater than the lookahead days amount, break the loop
          if (index >= lookaheadDays) {
            break
          }

          // Skip past dates - don't generate slots for dates before today
          const slotDate = moment(startDate).add(index, `days`)
          if (slotDate.isBefore(moment().startOf(`day`))) {
            continue
          }

          // Initialize slot if it doesn't exist
          if (!allSlots[index]) {
            allSlots[index] = { timeSlots: [], date: slotDate.format(`YYYY-MM-DD`) }
          }

          // Generate time slots for each hour block
          const timeSlots = hours
            .map((h) => {
              // Round the start and end times to the granularity
              const roundedStart = moment(h.from, `HH:mm`).minute(
                Math.ceil(moment(h.from, `HH:mm`).minute() / schedulingConfiguration.granularity!) *
                  schedulingConfiguration.granularity!,
              )
              const roundedEnd = moment(h.to, `HH:mm`).minute(
                Math.floor(moment(h.to, `HH:mm`).minute() / schedulingConfiguration.granularity!) *
                  schedulingConfiguration.granularity!,
              )

              // Create a slot for each granularity interval between start and end times
              const slots: TimeSlotType[] = []
              // Reuse slotDate from outer scope (already calculated)
              const slotDateStr = slotDate.format(`YYYY-MM-DD`)
              for (
                let time = roundedStart.clone();
                time.isBefore(roundedEnd);
                time.add(schedulingConfiguration.granularity!, `minutes`)
              ) {
                // For the current day, skip slots before current time + 1 hour
                if (slotDateStr === moment().format(`YYYY-MM-DD`)) {
                  const slotTime = moment.tz(`${slotDateStr} ${time.format(`HH:mm`)}`, locationTimezone)
                  const currentTime = moment().tz(userTimezone)
                  if (slotTime.isBefore(currentTime.add(1, `hour`))) {
                    continue
                  }
                }
                slots.push({ time: time.format(`h:mm A`), availabilities: [availability] })
              }
              return slots
            })
            .flat()

          // Merge time slots into the day slot
          timeSlots.forEach((newSlot) => {
            const existingSlot = allSlots[index].timeSlots.find((slot) => slot.time === newSlot.time)
            if (existingSlot) {
              existingSlot.availabilities = Array.from(
                new Set([...existingSlot.availabilities, ...newSlot.availabilities]),
              )
            } else {
              allSlots[index].timeSlots.push(newSlot)
            }
          })
        }
      })
    })
  })

  // Fill in any missing slots with empty day slots
  for (let i = 0; i < lookaheadDays; i++) {
    if (!allSlots[i]) {
      allSlots[i] = {
        timeSlots: [],
        date: moment(startDate).add(i, `days`).format(`YYYY-MM-DD`),
      }
    }
  }

  // Track all valid availabilities across all appointment types after conflict checking
  const validAvailabilitiesBySlot = new Map<string, Set<string>>() // key: "date|time", value: Set of availability IDs
  const validAppointmentTypesBySlotAndAvailability = new Map<string, Map<string, Set<string>>>() // slotKey -> (availabilityKey -> Set<appointmentTypeVendorId>)

  // Apply removeOverlaps to filter conflicts for each appointment type
  appointmentTypes.forEach((appointmentType) => {
    // Create deep copies to avoid mutating the original allSlots
    const appointmentTypeSlots = allSlots.map((slot) => ({
      ...slot,
      timeSlots:
        slot?.timeSlots
          ?.filter((timeSlot) =>
            timeSlot.availabilities.some((avail) => avail.appointmentTypeIds?.includes(appointmentType.vendorId!)),
          )
          .map((timeSlot) => ({
            ...timeSlot,
            // IMPORTANT: Filter availabilities to only those that support this appointment type
            availabilities: timeSlot.availabilities.filter((avail) =>
              avail.appointmentTypeIds?.includes(appointmentType.vendorId!),
            ),
          })) || [],
    }))

    // Apply conflict resolution for this appointment type
    removeOverlaps(
      appointmentTypeSlots,
      appointments as KollaAppointment[],
      schedules as ResourceScheduleJson[],
      providers,
      appointmentType,
      schedulingConfiguration,
      schedulingConfiguration?.allowDoubleBooking,
    )

    // Collect valid availabilities from the filtered slots
    appointmentTypeSlots.forEach((daySlot) => {
      daySlot.timeSlots.forEach((timeSlot) => {
        const slotKey = `${daySlot.date}|${timeSlot.time}`
        if (!validAvailabilitiesBySlot.has(slotKey)) {
          validAvailabilitiesBySlot.set(slotKey, new Set())
        }
        if (!validAppointmentTypesBySlotAndAvailability.has(slotKey)) {
          validAppointmentTypesBySlotAndAvailability.set(slotKey, new Map())
        }

        timeSlot.availabilities.forEach((avail) => {
          // Create unique key for availability
          const availKey = `${avail.providerId}|${avail.operatoryId}`

          validAvailabilitiesBySlot.get(slotKey)!.add(availKey)

          const availabilityTypes = validAppointmentTypesBySlotAndAvailability.get(slotKey)!
          if (!availabilityTypes.has(availKey)) {
            availabilityTypes.set(availKey, new Set())
          }
          availabilityTypes.get(availKey)!.add(appointmentType.vendorId!)
        })
      })
    })

    debug(
      `scheduling`,
      `generateAllSlots:appointment-type-filtered`,
      JSON.stringify({
        appointmentTypeId: appointmentType.vendorId,
        slotsWithAvailability: appointmentTypeSlots
          .filter((s) => s.timeSlots.length > 0)
          .map((s) => ({
            date: s.date,
            times: s.timeSlots.map((t) => t.time),
          })),
      }),
    )
  })

  debug(
    `scheduling`,
    `generateAllSlots:valid-availabilities`,
    JSON.stringify({
      totalSlotKeys: validAvailabilitiesBySlot.size,
      sampleKeys: Array.from(validAvailabilitiesBySlot.keys()).slice(0, 10),
    }),
  )

  // For each appointment type, propagate its validity to consecutive future slots needed for its duration
  // This ensures client-side duration validation can find the appointment type in all required consecutive slots
  const granularity = schedulingConfiguration!.granularity!
  appointmentTypes.forEach((appointmentType) => {
    const slotsNeeded = Math.ceil((appointmentType.appointmentLength || granularity) / granularity)
    if (slotsNeeded <= 1) return // No propagation needed for single-slot appointments

    allSlots.forEach((daySlot) => {
      daySlot.timeSlots.forEach((timeSlot, idx) => {
        const slotKey = `${daySlot.date}|${timeSlot.time}`
        const slotValidTypes = validAppointmentTypesBySlotAndAvailability.get(slotKey)
        if (!slotValidTypes) return

        // For each availability at this starting slot that supports this appointment type
        timeSlot.availabilities.forEach((avail) => {
          const availKey = `${avail.providerId}|${avail.operatoryId}`
          const availTypes = slotValidTypes.get(availKey)
          if (!availTypes?.has(appointmentType.vendorId!)) return

          // Propagate this appointment type to the next N-1 consecutive slots for this availability
          for (let i = 1; i < slotsNeeded; i++) {
            const futureSlot = daySlot.timeSlots[idx + i]
            if (!futureSlot) break

            // Verify slots are consecutive in time
            const expectedMinutesDiff = granularity * i
            const actualMinutesDiff = moment(futureSlot.time, `h:mm A`).diff(moment(timeSlot.time, `h:mm A`), `minutes`)
            if (actualMinutesDiff !== expectedMinutesDiff) break

            // Check if this future slot has the same availability (provider+operatory)
            const hasSameAvailability = futureSlot.availabilities.some(
              (futureAvail) =>
                futureAvail.providerId === avail.providerId && futureAvail.operatoryId === avail.operatoryId,
            )
            if (!hasSameAvailability) break

            // Mark this appointment type as valid for this availability at the future slot
            const futureSlotKey = `${daySlot.date}|${futureSlot.time}`
            if (!validAppointmentTypesBySlotAndAvailability.has(futureSlotKey)) {
              validAppointmentTypesBySlotAndAvailability.set(futureSlotKey, new Map())
            }
            const futureSlotTypes = validAppointmentTypesBySlotAndAvailability.get(futureSlotKey)!
            if (!futureSlotTypes.has(availKey)) {
              futureSlotTypes.set(availKey, new Set())
            }
            futureSlotTypes.get(availKey)!.add(appointmentType.vendorId!)
          }
        })
      })
    })
  })

  // Now filter the original allSlots to only include availabilities that passed checks for at least one appointment type
  allSlots.forEach((daySlot) => {
    daySlot.timeSlots = daySlot.timeSlots.filter((timeSlot) => {
      const slotKey = `${daySlot.date}|${timeSlot.time}`
      const validAvails = validAvailabilitiesBySlot.get(slotKey)

      if (!validAvails || validAvails.size === 0) {
        return false
      }

      // Filter availabilities to only those that passed conflict checks
      timeSlot.availabilities = compact(
        timeSlot.availabilities.map((avail) => {
          const availKey = `${avail.providerId}|${avail.operatoryId}`
          if (!validAvails.has(availKey)) {
            return null
          }

          const validVendorIds = validAppointmentTypesBySlotAndAvailability.get(slotKey)?.get(availKey)

          if (!validVendorIds || validVendorIds.size === 0) {
            return null
          }

          const filteredAppointmentTypeIds =
            avail.appointmentTypeIds?.filter((id) => {
              return validVendorIds.has(id)
            }) ?? []

          if (filteredAppointmentTypeIds.length === 0) {
            return null
          }
          // Return a NEW object to avoid mutating shared availability references
          // Use toJSON() if available (Dyngoose models) to properly copy aliased properties like providerId
          const availAny = avail as any
          const plainAvail = typeof availAny.toJSON === `function` ? availAny.toJSON() : avail
          return {
            ...plainAvail,
            appointmentTypeIds: filteredAppointmentTypeIds,
          }
        }),
      )

      return timeSlot.availabilities.length > 0
    })
  })

  // Sort time slots within each day by time
  allSlots.forEach((daySlot) => {
    daySlot.timeSlots.sort((a, b) => {
      const timeA = moment(a.time, `h:mm A`)
      const timeB = moment(b.time, `h:mm A`)
      return timeA.valueOf() - timeB.valueOf()
    })
  })

  return allSlots
}

/**
 * Loads and processes all scheduling data server-side, including slot generation and conflict resolution.
 * This replaces the client-side processing logic from SchedulingWizard with server-side processing.
 *
 * @param locationId - The location ID to load scheduling data for
 * @param day - Optional start date (defaults to today)
 * @param kollaService - Optional KollaService instance for testing (will create one if not provided)
 * @returns Fully processed scheduling data with slots ready for display
 */
export async function loadSchedulingDataProcessed(
  locationId: string,
  day?: string,
  kollaService?: KollaService,
): Promise<ProcessedSchedulingData> {
  const START_TIME = performance.now()
  const timings: Record<string, number> = {}

  const validDay = day ?? moment().format(`YYYY-MM-DD`)
  // Allow dates within the current month or future, but reset dates before the current month
  if (!moment(validDay).isValid() || moment(validDay).isBefore(moment().startOf(`month`))) {
    logWarn(`Invalid day parameter`, day, `setting to start of current month`)
    day = moment().startOf(`month`).format(`YYYY-MM-DD`)
  } else {
    day = validDay
  }
  const lookaheadDays = day ? moment(day).endOf(`month`).diff(day, `days`) + 1 : LOOKAHEAD_DAYS

  if (!locationId) {
    logWarn(`loadSchedulingDataProcessed: locationId is empty or null`, { locationId })
    throw redirect(`/`)
  }

  // Location and workspace validation (unchanged from original)
  const locationStart = performance.now()
  const location = await Location.getById(locationId, undefined)
  timings.location = performance.now() - locationStart

  if (!location) {
    logWarn(`loadSchedulingDataProcessed: location not found`, { locationId })
    throw redirect(`/`)
  }
  if (!location.isSchedulerLive) {
    logWarn(`scheduling is not enabled for location`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }

  const workspaceStart = performance.now()
  const workspace = await Workspace.getById(location.workspaceId, location.orgId)
  timings.workspace = performance.now() - workspaceStart

  if (!workspace || !workspace.products?.includes(`Scheduling`)) {
    logWarn(`scheduling is not enabled for workspace`, location.workspaceId)
    throw redirect(`/scheduling/unavailable`)
  }

  // Load appointment types (unchanged from original)
  const appointmentTypesStart = performance.now()
  let { hits: appointmentTypes } = await search.query(
    AppointmentType,
    [
      `orgId:${location.orgId}`,
      `workspaceId:${location.workspaceId}`,
      `kollaConsumerId:${location.kollaConsumerId}`,
      `activeScheduler:true`,
      `vendor:kolla`,
      `!archived:true`,
    ],
    {
      limit: 1000,
      page: 0,
      sort: `displayName.keyword`,
    },
  )
  timings.appointmentTypesQuery = performance.now() - appointmentTypesStart

  // Filter appointment types against external types (unchanged from original)
  const externalTypesStart = performance.now()
  const actualKollaService = kollaService || (await KollaService.getInstance(location.orgId!))
  const externalAppointmentTypes = await actualKollaService.getAppointmentTypes(location)
  timings.externalAppointmentTypes = performance.now() - externalTypesStart

  if (!externalAppointmentTypes) {
    logWarn(`failed to get external appointment types`, location.id)
    throw redirect(`/scheduling/unavailable`)
  }
  appointmentTypes = appointmentTypes.filter(
    (at) => !!externalAppointmentTypes.find((eat) => eat.remote_id === at.vendorId),
  )

  // Load database data in parallel (no retry needed)
  const dbDataStart = performance.now()
  const [internalSchedules, schedulingConfiguration] = await Promise.all([
    getInternalSchedules(location, day),
    search.findOne(SchedulingConfiguration, [
      `locationId:${location.id}`,
    ]) as Promise<SchedulingConfigurationJson | null>,
  ])
  timings.dbDataLoad = performance.now() - dbDataStart

  // Load providers and availabilities from location (these are cached/DB queries, not PMS)
  const providerDataStart = performance.now()
  const [providers, availabilities] = await Promise.all([
    location.getProviders(`scheduler`),
    location.getAvailabilities(`scheduler`),
  ])
  timings.providerDataLoad = performance.now() - providerDataStart

  // Load PMS appointments with retry logic and 1-minute timeout
  const pmsDataStart = performance.now()
  const externalAppointments = (await retry(async () => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`PMS appointments load timeout after 1 minute`)), 60000)
    })

    const dataPromise = actualKollaService.getAppointments(location, moment(day), moment(day).add(1, `month`))

    return Promise.race([dataPromise, timeoutPromise])
  }, 3)) as Partial<KollaAppointment>[]
  timings.pmsDataLoad = performance.now() - pmsDataStart

  // Generate slots server-side (moved from SchedulingWizard)
  const slotsStart = performance.now()
  const slots = generateAllSlots(
    appointmentTypes,
    providers,
    availabilities,
    externalAppointments,
    internalSchedules,
    location,
    day,
    lookaheadDays,
    schedulingConfiguration,
  )
  timings.slotsGeneration = performance.now() - slotsStart

  // S3 logo URL (async)
  const s3 = new S3()
  const s3Params = {
    Bucket: `trackable-${envName()}-assets`,
    Key: `scheduling-logos/locations/${location.id}`,
  }

  const TOTAL_TIME = performance.now() - START_TIME
  logWarn(`Scheduling Page Loader Processed Timings:`, {
    total: TOTAL_TIME,
    ...timings,
  })

  return {
    day,
    lookaheadDays,
    appointmentTypes,
    logoUrl: s3.getSignedUrlPromise(`getObject`, s3Params),
    providers,
    availabilities,
    slots,
    schedulingConfiguration,
    timing: { total: TOTAL_TIME, ...timings },
  }
}

/**
 * Calculates available time slots for agent booking
 * Extracted from the GET availabilities endpoint to ensure consistency with POST booking endpoint
 */
export async function calculateAgentAvailabilities({
  location,
  appointmentType,
  availabilities,
  providers,
  appointments,
  schedules,
  allowDoubleBooking,
  startDate,
  endDate,
}: {
  location: Location
  appointmentType: AppointmentTypeType
  availabilities: AvailabilityType[]
  providers: ProviderType[]
  appointments: KollaAppointment[] | undefined
  schedules: ResourceScheduleJson[]
  allowDoubleBooking: boolean
  startDate: moment.Moment
  endDate: moment.Moment
}): Promise<DaySlotType[]> {
  const range = Math.abs(startDate.diff(endDate, `days`)) + 1

  // Filter availabilities for this appointment type
  const filteredAvailabilities = availabilities.filter((a) => a.appointmentTypeIds?.includes(appointmentType.vendorId!))

  // calculate slots
  let slots: (DaySlotType & { date?: string })[] = new Array(range)
  // get the day of the week for the current day
  const firstDay = startDate.day()

  // for each availability, go over every day of the week in its timeSlots object:
  filteredAvailabilities.forEach((availability) => {
    Object.entries(availability.timeSlots!).forEach(([dayOfTheWeek, { hours, open }]) => {
      // if the provider doesn't have timeslots for this day, skip it
      if (!open) {
        return
      }
      // get the day of the week for the availability timeslots. i.e. if the availability is for Tuesday, the day is 2
      const day = moment(startDate).day(dayOfTheWeek).day()
      // calculate the offset from the current day to the day of the week of the availability. I.e. if today is Monday and the availability is for Tuesday, the offset is 1
      const offset = (day + 7 - firstDay) % 7
      // access the slots array at the offset and every 7 days after that, up to the range amount.
      for (let i = 0; i <= range / 7; i++) {
        const index = offset + i * 7
        // if the index is greater than the range amount, break the loop, we're done.
        if (index >= range) {
          break
        }
        // if the slot at the index doesn't exist, initialize it
        if (!slots[index]) {
          slots[index] = { timeSlots: [], date: moment(startDate).add(index, `days`).format(`YYYY-MM-DD`) }
        }
        // get all the slots for the day
        const timeSlots = hours
          .map((h) => {
            // round the start and end times to the granularity. I.e. if the granularity is 30 minutes, 9:15 start time becomes 9:30 and 17:45 end time becomes 17:30
            const roundedStart = moment(h.from, `HH:mm`).minute(
              Math.ceil(moment(h.from, `HH:mm`).minute() / GRANULARITY) * GRANULARITY,
            )
            const roundedEnd = moment(h.to, `HH:mm`).minute(
              Math.floor(moment(h.to, `HH:mm`).minute() / GRANULARITY) * GRANULARITY,
            )
            // create a slot for each GRANULARITY interval between the start and end times
            const slots: TimeSlotType[] = []
            for (let time = roundedStart.clone(); time.isBefore(roundedEnd); time.add(GRANULARITY, `minutes`)) {
              // for the current day, if the slot time is before the current time + 1 hour, skip it
              if (startDate.format(`YYYY-MM-DD`) === moment().format(`YYYY-MM-DD`) && offset === 0) {
                if (time.isBefore(moment().add(1, `hour`))) {
                  continue
                }
              }
              slots.push({ time: time.format(`h:mm A`), availabilities: [availability] })
            }
            return slots
          })
          .flat()
        // merge them into the slots array at the index
        timeSlots.forEach((newSlot) => {
          const existingSlot = slots[index].timeSlots.find((slot) => slot.time === newSlot.time)
          if (existingSlot) {
            existingSlot.availabilities = Array.from(
              new Set([...existingSlot.availabilities, ...newSlot.availabilities]),
            )
          } else {
            slots[index].timeSlots.push(newSlot)
          }
        })
      }
    })
  })

  // Apply removeOverlaps logic
  // Agent uses default GRANULARITY constant, not SchedulingConfiguration
  removeOverlaps(slots, appointments, schedules, providers, appointmentType, null, allowDoubleBooking)

  // remove slots with no availabilities
  slots = slots.filter((slot) => slot.timeSlots.length > 0)

  return slots.filter((slot) => slot !== undefined) as DaySlotType[]
}
